import React, { useState } from 'react';
import { Plus, Search, Edit2, Trash2, Phone, User, Building2, MapPin, Eye, EyeOff, Download, Filter, X, FileText, FileSpreadsheet, ChevronDown, Upload, History, Smartphone, AlertTriangle, Settings } from 'lucide-react';
import { apiService } from '../services/api';
import { GSMLine, User as UserType } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import Modal from './Modal';
import GSMNewSubscription from './GSMNewSubscription';
import CollapsibleFilters from './CollapsibleFilters';
import { useLanguage } from '../contexts/LanguageContext';

const GSMLinesPage: React.FC = () => {
  const { t, language } = useLanguage();
  const { user: currentUser } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('gsm-lines', setSearchTerm);
  const [showModal, setShowModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedGSMLineForHistory, setSelectedGSMLineForHistory] = useState<GSMLine | null>(null);
  const [gsmLineHistory, setGsmLineHistory] = useState<any[]>([]);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [showQuickAssignModal, setShowQuickAssignModal] = useState(false);
  const [selectedGSMLineForAssign, setSelectedGSMLineForAssign] = useState<GSMLine | null>(null);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number>(0);
  const [forwardedGsmSearchTerm, setForwardedGsmSearchTerm] = useState('');
  const [showForwardedGsmDropdown, setShowForwardedGsmDropdown] = useState(false);
  

  const [editingGSMLine, setEditingGSMLine] = useState<GSMLine | null>(null);
  const [showPinPuk, setShowPinPuk] = useState<{[key: number]: boolean}>({});
  const [filterLocation, setFilterLocation] = useState<number>(0);
  const [filterUser, setFilterUser] = useState<number>(0);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterCompany, setFilterCompany] = useState<number>(0);
  const [filterTariff, setFilterTariff] = useState<number>(0);
  const [filterLongUnassigned, setFilterLongUnassigned] = useState<boolean>(false);
  const [showGsmSettingsModal, setShowGsmSettingsModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [emptyLineTariffId, setEmptyLineTariffId] = useState<number>(0);
  const [excludedStatuses, setExcludedStatuses] = useState<string[]>(['reserved', 'forwarded', 'transferred']);
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [gsmLinesData, setGsmLinesData] = useState<GSMLine[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [tariffs, setTariffs] = useState<any[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Load data on component mount
  React.useEffect(() => {
    loadData();
    
    // Tarife filtresini kontrol et
    const savedFilter = localStorage.getItem('gsmLinesFilter');
    if (savedFilter) {
      try {
        const filter = JSON.parse(savedFilter);
        if (filter.tariff_id) {
          // Tarife filtresini uygula
          setFilterTariff(filter.tariff_id);
          showGlobalNotification.info(
            t('gsm.tariffFilterApplied'),
            `"${filter.tariff_name}" ${t('gsm.tariffFilterMsg')}`
          );
        }
        // Filtreyi temizle
        localStorage.removeItem('gsmLinesFilter');
      } catch (error) {
        console.error('Filter parse error:', error);
      }
    }
  }, []);

  // Bildirimden belirli bir GSM hattı açma
  React.useEffect(() => {
    const handler = async (e: CustomEvent) => {
      if (e.detail?.page !== 'gsm-lines' || !e.detail?.entityId) return;
      try {
        const line = await apiService.getGSMLineById(e.detail.entityId);
        if (line) {
          setSelectedGSMLineForHistory(line);
          setShowAllHistory(false);
          setShowHistoryModal(true);
          apiService.getGSMLineHistory(line.id).then(data => setGsmLineHistory(data)).catch(() => {});
        }
      } catch (err) { console.error('[openEntityById gsmlines]', err); }
    };
    window.addEventListener('openEntityById', handler as EventListener);
    return () => window.removeEventListener('openEntityById', handler as EventListener);
  }, []);

  // Close forwarded GSM dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.forwarded-gsm-dropdown')) {
        setShowForwardedGsmDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [gsmResponse, companiesResponse, locationsResponse, tariffsResponse, usersResponse, settingsResponse] = await Promise.all([
        apiService.getGSMLines(),
        apiService.getCompanies(),
        apiService.getLocations(),
        apiService.getTariffs(),
        apiService.getUsers(),
        apiService.getSystemSettings()
      ]);

      setGsmLinesData(Array.isArray(gsmResponse) ? gsmResponse : []);
      setCompanies(Array.isArray(companiesResponse) ? companiesResponse : []);
      setLocations(Array.isArray(locationsResponse) ? locationsResponse : []);
      setTariffs(Array.isArray(tariffsResponse) ? tariffsResponse : []);
      setAllUsers(Array.isArray(usersResponse) ? usersResponse : []);

      if (Array.isArray(settingsResponse)) {
        const tariffSetting = settingsResponse.find((s: any) => s.setting_key === 'gsm_empty_line_tariff_id');
        if (tariffSetting) {
          setEmptyLineTariffId(parseInt(tariffSetting.setting_value) || 0);
        }
        const excludedSetting = settingsResponse.find((s: any) => s.setting_key === 'gsm_excluded_statuses');
        if (excludedSetting) {
          try {
            const parsed = JSON.parse(excludedSetting.setting_value);
            if (Array.isArray(parsed)) setExcludedStatuses(parsed);
          } catch {}
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('common.error'), t('gsm.errLoadData'));
    } finally {
      setLoading(false);
    }
  };
  
  const [gsmData, setGsmData] = useState({
    phone_number: '',
    tariff_id: 0,
    company_id: 0,
    user_id: 0,
    location_id: 0,
    pin_code: '',
    puk_code: '',
    status: 'active' as 'active' | 'inactive' | 'suspended' | 'reserved' | 'available' | 'transferred' | 'forwarded',
    forwarded_to_gsm_id: 0,
    description: ''
  });

  const [importData, setImportData] = useState({
    file: null as File | null,
    preview: [] as any[],
    errors: [] as string[],
    processing: false
  });

  // GSM numarası maskeleme fonksiyonu
  const formatPhoneNumber = (value: string) => {
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');
    
    // Türk telefon numarası formatı: 0532 123 45 67
    if (numbers.length <= 4) {
      return numbers;
    } else if (numbers.length <= 7) {
      return `${numbers.slice(0, 4)} ${numbers.slice(4)}`;
    } else if (numbers.length <= 9) {
      return `${numbers.slice(0, 4)} ${numbers.slice(4, 7)} ${numbers.slice(7)}`;
    } else {
      return `${numbers.slice(0, 4)} ${numbers.slice(4, 7)} ${numbers.slice(7, 9)} ${numbers.slice(9, 11)}`;
    }
  };

  // GSM numarası değişiklik handler'ı
  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedValue = formatPhoneNumber(value);
    
    // Maksimum 13 karakter (0532 123 45 67)
    if (formattedValue.length <= 13) {
      setGsmData({...gsmData, phone_number: formattedValue});
    }
  };

  // GSM numarası validasyon fonksiyonu
  const validatePhoneNumber = (phoneNumber: string) => {
    const numbers = phoneNumber.replace(/\D/g, '');
    // +90 549 802 72 11 = 90 + 10 haneli = 12 haneli toplam
    return numbers.length === 12 && numbers.startsWith('90') && numbers.substring(2).length === 10;
  };
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    location_id: 0,
    phone: '',
    title: '',
    role: 'calisan' as 'admin' | 'superadmin' | 'teknisyen' | 'calisan',
    password: ''
  });

  const logActivity = (action: string, description: string, phoneNumber?: string) => {
    if (!currentUser) return;
    
    // Show toast notification
    showGlobalNotification.success(
      t('gsm.notifLineOp'),
      description
    );
  };

  // GSM numarası formatla ve normalize et
  const normalizePhoneNumber = (input: string) => {
    const numbers = input.replace(/\D/g, '');
    
    // Eğer 11 haneli ve 0 ile başlıyorsa (05491726950), 0'ı kaldır ve +90 ekle
    if (numbers.length === 11 && numbers.startsWith('0')) {
      const withoutZero = numbers.substring(1);
      return `+90 ${withoutZero.substring(0, 3)} ${withoutZero.substring(3, 6)} ${withoutZero.substring(6, 8)} ${withoutZero.substring(8)}`;
    }
    
    // Eğer 10 haneli ise +90 ekle
    if (numbers.length === 10) {
      return `+90 ${numbers.substring(0, 3)} ${numbers.substring(3, 6)} ${numbers.substring(6, 8)} ${numbers.substring(8)}`;
    }
    
    // Eğer 12 haneli ve 90 ile başlıyorsa +90 ekle
    if (numbers.length === 12 && numbers.startsWith('90')) {
      const phoneNumber = numbers.substring(2);
      return `+90 ${phoneNumber.substring(0, 3)} ${phoneNumber.substring(3, 6)} ${phoneNumber.substring(6, 8)} ${phoneNumber.substring(8)}`;
    }
    
    return input;
  };

  const filteredGSMLines = (gsmLinesData || []).filter(line => {
    let searchMatch = false;
    
    if (searchTerm) {
      // Normal arama
      const normalSearch = line.phone_number.includes(searchTerm) ||
        line.user?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        line.company?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        line.tariff?.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      // GSM numarası arama - sadece rakam içeriyorsa
      const hasNumbers = /\d/.test(searchTerm);
      let phoneMatch = false;
      
      if (hasNumbers) {
        const normalizedSearch = normalizePhoneNumber(searchTerm);
        phoneMatch = line.phone_number.includes(normalizedSearch) || 
                    line.phone_number.replace(/\D/g, '').includes(searchTerm.replace(/\D/g, ''));
      }
      
      searchMatch = normalSearch || phoneMatch;
    } else {
      searchMatch = true;
    }
    
    const matchesLocation = filterLocation === 0 || line.location_id === filterLocation;
    const matchesUser = filterUser === 0 ||
                       (filterUser === -1 && !line.user_id) ||
                       line.user_id === filterUser;
    const matchesStatus = filterStatus === 'all' || line.status === filterStatus;
    const matchesCompany = filterCompany === 0 || line.company_id === filterCompany;
    const matchesTariff = filterTariff === 0 || line.tariff_id === filterTariff;

    let matchesLongUnassigned = true;
    if (filterLongUnassigned) {
      if (excludedStatuses.includes(line.status)) {
        matchesLongUnassigned = false;
      } else {
        const threeMonthsAgo = new Date();
        threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
        const isUnassignedOrAvailable = !line.user_id || line.status === 'available';
        const isOldEnough = new Date(line.updated_at) < threeMonthsAgo;
        matchesLongUnassigned = isUnassignedOrAvailable && isOldEnough;
      }
    }

    return searchMatch && matchesLocation && matchesUser && matchesStatus && matchesCompany && matchesTariff && matchesLongUnassigned;
  });

  const handleEdit = (gsmLine: GSMLine) => {
    setEditingGSMLine(gsmLine);
    setGsmData({
      phone_number: gsmLine.phone_number,
      tariff_id: gsmLine.tariff_id,
      company_id: gsmLine.company_id,
      user_id: gsmLine.user_id || 0,
      location_id: gsmLine.location_id,
      pin_code: gsmLine.pin_code,
      puk_code: gsmLine.puk_code,
      status: gsmLine.status,
      forwarded_to_gsm_id: gsmLine.forwarded_to_gsm_id || 0,
      description: gsmLine.description || ''
    });
    
    // Eğer yönlendirilmiş durumda ise, seçili GSM hattının bilgisini arama kutusunda göster
    if (gsmLine.status === 'forwarded' && gsmLine.forwarded_to_gsm_id) {
      const forwardedLine = gsmLinesData.find(line => line.id === gsmLine.forwarded_to_gsm_id);
      if (forwardedLine) {
        setForwardedGsmSearchTerm(`${forwardedLine.phone_number} - ${forwardedLine.company?.name}`);
      } else {
        setForwardedGsmSearchTerm('');
      }
    } else {
      setForwardedGsmSearchTerm('');
    }
    
    setShowModal(true);
    setShowForwardedGsmDropdown(false);
  };

  const handleAdd = () => {
    setEditingGSMLine(null);
    setGsmData({
      phone_number: '',
      tariff_id: 0,
      company_id: 0,
      user_id: 0,
      location_id: 0,
      pin_code: '',
      puk_code: '',
      status: 'active',
      forwarded_to_gsm_id: 0,
      description: ''
    });
    setShowModal(true);
    setForwardedGsmSearchTerm('');
    setShowForwardedGsmDropdown(false);
  };

  const handleImport = () => {
    setImportData({
      file: null,
      preview: [],
      errors: [],
      processing: false
    });
    setShowImportModal(true);
  };

  const downloadTemplate = () => {
    const headers = [
      'gsm_number',
      'tariff_name', 
      'company_name',
      'location_name',
      'user_name',
      'user_email',
      'user_phone',
      'user_title',
      'user_department',
      'pin_code',
      'puk_code',
      'status',
      'description'
    ];
    
    const sampleData = [
      [
        '0532 123 45 678',
        'Kurumsal 20GB',
        'TechCorp A.Ş.',
        'İstanbul Merkez',
        'Ahmet Yılmaz',
        'ahmet@techcorp.com',
        '0532 987 65 43',
        'IT Uzmanı',
        'Bilgi İşlem',
        '1234',
        '12345678',
        'active',
        'Yeni hat açılışı'
      ],
      [
        '0549 802 72 1234',
        'Kurumsal 50GB',
        'InnoTech Ltd.',
        'Ankara Şube',
        'Mehmet Demir',
        'mehmet@innotech.com',
        '0549 123 45 67',
        'Proje Yöneticisi',
        'Proje Yönetimi',
        '5678',
        '87654321',
        'active',
        'Proje ekibi için'
      ]
    ];

    const BOM = '\uFEFF';
    const csvContent = BOM + [headers, ...sampleData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'gsm-hatları-import-şablonu.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showGlobalNotification.success(t('common.success'), t('gsm.templateDownloaded'));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv') && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showGlobalNotification.error(t('common.error'), t('gsm.errFileType'));
      return;
    }

    setImportData(prev => ({ ...prev, file, errors: [] }));

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      parseCSVData(text);
    };
    reader.readAsText(file);
  };

  const parseCSVData = (csvText: string) => {
    try {
      const lines = csvText.split('\n').filter(line => line.trim());
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const dataLines = lines.slice(1);

      const preview = dataLines.map((line, index) => {
        const values = line.split(',').map(v => v.replace(/"/g, '').trim());
        const row: any = { _rowIndex: index + 2 };
        
        headers.forEach((header, i) => {
          row[header] = values[i] || '';
        });
        
        return row;
      });

      setImportData(prev => ({ ...prev, preview }));
      validateImportData(preview);
    } catch (error) {
      showGlobalNotification.error(t('common.error'), t('gsm.errCsvRead'));
    }
  };

  const validateImportData = (data: any[]) => {
    const errors: string[] = [];
    
    data.forEach((row, index) => {
      const rowNum = index + 2;
      
      // GSM numarası kontrolü
      if (!row.gsm_number) {
        errors.push(`Satır ${rowNum}: GSM numarası boş olamaz`);
      } else if (!/^0\d{12}$/.test(row.gsm_number.replace(/\s/g, ''))) {
        errors.push(`Satır ${rowNum}: GSM numarası formatı hatalı (0XXX XXX XX XXXX)`);
      }
      
      // Tarife kontrolü
      if (!row.tariff_name) {
        errors.push(`Satır ${rowNum}: Tarife adı boş olamaz`);
      }
      
      // Şirket kontrolü
      if (!row.company_name) {
        errors.push(`Satır ${rowNum}: Şirket adı boş olamaz`);
      }
      
      // Lokasyon kontrolü
      if (!row.location_name) {
        errors.push(`Satır ${rowNum}: Lokasyon adı boş olamaz`);
      }
      
      // Durum kontrolü
      const validStatuses = ['active', 'inactive', 'suspended', 'reserved', 'available'];
      if (row.status && !validStatuses.includes(row.status)) {
        errors.push(`Satır ${rowNum}: Geçersiz durum (${validStatuses.join(', ')})`);
      }
      
      // Kullanıcı e-posta kontrolü (varsa)
      if (row.user_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.user_email)) {
        errors.push(`Satır ${rowNum}: Geçersiz e-posta formatı`);
      }
    });
    
    setImportData(prev => ({ ...prev, errors }));
  };

  // GSM numara format fonksiyonu
  const formatGSMNumber = (phone: string) => {
    if (!phone) return phone;
    
    // Sadece rakamları al
    const numbers = phone.replace(/\D/g, '');
    
    // 11 haneli ve 0 ile başlıyorsa (05001234567), 0'ı kaldır
    if (numbers.length === 11 && numbers.startsWith('0')) {
      const withoutZero = numbers.substring(1);
      return `+90 ${withoutZero.substring(0, 3)} ${withoutZero.substring(3, 6)} ${withoutZero.substring(6, 8)} ${withoutZero.substring(8)}`;
    }
    
    // 10 haneli ise (5001234567) +90 ekle ve formatla
    if (numbers.length === 10) {
      return `+90 ${numbers.substring(0, 3)} ${numbers.substring(3, 6)} ${numbers.substring(6, 8)} ${numbers.substring(8)}`;
    }
    
    // 12 haneli ve 90 ile başlıyorsa (905001234567)
    if (numbers.length === 12 && numbers.startsWith('90')) {
      const phoneNumber = numbers.substring(2);
      return `+90 ${phoneNumber.substring(0, 3)} ${phoneNumber.substring(3, 6)} ${phoneNumber.substring(6, 8)} ${phoneNumber.substring(8)}`;
    }
    
    return phone; // Formatlanamadıysa olduğu gibi döndür
  };

  const processImport = async () => {
    if (importData.errors.length > 0) {
      showGlobalNotification.error(t('common.error'), t('gsm.errFixErrors'));
      return;
    }

    setImportData(prev => ({ ...prev, processing: true }));

    try {
      let successCount = 0;
      let userCreatedCount = 0;

      for (const row of importData.preview) {
        // Şirket bul veya oluştur
        let company = companies.find(c => c.name === row.company_name);
        if (!company) {
          showGlobalNotification.warning(t('common.warning'), `${language === 'en' ? 'Company not found' : 'Şirket bulunamadı'}: ${row.company_name}`);
          continue;
        }

        // Lokasyon bul veya oluştur
        let location = locations.find(l => l.name === row.location_name);
        if (!location) {
          showGlobalNotification.warning(t('common.warning'), `${language === 'en' ? 'Location not found' : 'Lokasyon bulunamadı'}: ${row.location_name}`);
          continue;
        }

        // Tarife bul
        let tariff = tariffs.find(tariffItem => tariffItem.name === row.tariff_name);
        if (!tariff) {
          showGlobalNotification.warning(t('common.warning'), `${language === 'en' ? 'Tariff not found' : 'Tarife bulunamadı'}: ${row.tariff_name}`);
          continue;
        }

        // Kullanıcı bul veya oluştur
        let user = null;
        if (row.user_name && row.user_email && row.status !== 'reserved' && row.status !== 'available') {
          user = allUsers.find(u => u.email === row.user_email);
          
          if (!user) {
            // Yeni kullanıcı oluştur
            const newUser: UserType = {
              id: Math.max(...allUsers.map(u => u.id)) + 1,
              name: row.user_name,
              email: row.user_email,
              role: 'calisan',
              location_id: location.id,
              location: location,
              phone: row.user_phone || '',
              title: row.user_title || '',
              department: row.user_department || '',
              is_active: true,
              is_online: false,
              email_verified: false,
              two_factor_enabled: false,
              avatar: 'https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg?auto=compress&cs=tinysrgb&w=150&h=150&fit=crop',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            };
            
            allUsers.push(newUser);
            user = newUser;
            userCreatedCount++;
          }
        }

        // GSM hattı oluştur
        const newGSMLine: GSMLine = {
          id: Math.max(...gsmLines.map(g => g.id)) + 1,
          phone_number: row.gsm_number,
          tariff_id: tariff.id,
          tariff: tariff,
          company_id: company.id,
          company: company,
          user_id: user?.id || undefined,
          user: user || undefined,
          location_id: location.id,
          location: location,
          pin_code: row.pin_code || '0000',
          puk_code: row.puk_code || '00000000',
          status: (row.status as GSMLine['status']) || 'active',
          description: row.description || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        gsmLines.push(newGSMLine);
        successCount++;

        // Log kaydı
        logActivity(
          'create_gsm_line',
          `${user?.name || 'Sistem'} "${row.gsm_number}" GSM hattını toplu import ile oluşturdu`,
          row.gsm_number
        );
      }

      showGlobalNotification.success(
        language === 'en' ? 'Import Completed' : 'Import Tamamlandı',
        language === 'en'
          ? `${successCount} GSM lines added. ${userCreatedCount} new users created.`
          : `${successCount} GSM hattı eklendi. ${userCreatedCount} yeni kullanıcı oluşturuldu.`
      );

      setShowImportModal(false);
      setImportData({
        file: null,
        preview: [],
        errors: [],
        processing: false
      });

    } catch (error) {
      showGlobalNotification.error(t('common.error'), t('gsm.errImportFail'));
    } finally {
      setImportData(prev => ({ ...prev, processing: false }));
    }
  };

  const handleSave = async () => {
    // Validation
    if (!gsmData.phone_number || !gsmData.tariff_id || !gsmData.company_id || !gsmData.location_id) {
      showGlobalNotification.error(t('common.error'), t('gsm.errRequired'));
      return;
    }

    // GSM numarası format kontrolü
    if (!validatePhoneNumber(gsmData.phone_number)) {
      showGlobalNotification.error(t('common.error'), t('gsm.errPhoneFormat'));
      return;
    }

    try {
      if (editingGSMLine) {
        // Track changes for history
        const changes: any = {};
        const oldUser = editingGSMLine.user;
        const newUser = gsmData.user_id ? allUsers.find(u => u.id === gsmData.user_id) : undefined;
        const oldTariff = editingGSMLine.tariff;
        const newTariff = tariffs.find(t => t.id === gsmData.tariff_id);
        
        if (editingGSMLine.user_id !== gsmData.user_id) {
          if (!oldUser && newUser) {
            changes.user = `Atanmamış → ${newUser.name}`;
          } else if (oldUser && !newUser) {
            changes.user = `${oldUser.name} → Atanmamış`;
          } else if (oldUser && newUser) {
            changes.user = `${oldUser.name} → ${newUser.name}`;
          }
        }
        
        if (editingGSMLine.tariff_id !== gsmData.tariff_id && oldTariff && newTariff) {
          changes.tariff = `${oldTariff.name} → ${newTariff.name}`;
        }
        
        if (editingGSMLine.status !== gsmData.status) {
          changes.status = `${getStatusText(editingGSMLine.status)} → ${getStatusText(gsmData.status)}`;
        }
        
        if (editingGSMLine.pin_code !== gsmData.pin_code) {
          changes.pin_code = t('gsm.pinUpdated');
        }

        if (editingGSMLine.puk_code !== gsmData.puk_code) {
          changes.puk_code = t('gsm.pukUpdated');
        }
        
        await apiService.updateGSMLine(editingGSMLine.id, gsmData);
        
        // History will be automatically created by backend in user_activities table
        
        showGlobalNotification.success(t('common.success'), t('gsm.successUpdate'));
      } else {
        await apiService.createGSMLine(gsmData);
        showGlobalNotification.success(t('common.success'), t('gsm.successCreate'));
      }
      
      await loadData();
      setShowModal(false);
      setGsmData({
        phone_number: '',
        tariff_id: 0,
        company_id: 0,
        user_id: 0,
        location_id: 0,
        pin_code: '',
        puk_code: '',
        status: 'active',
        description: ''
      });
    } catch (error: any) {
      console.error('Error saving GSM line:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('gsm.errSaveLine'));
    }
  };

  const handleSaveOld = () => {
    if (editingGSMLine) {
      // Track changes for detailed logging
      const oldUser = editingGSMLine.user;
      const newUser = gsmData.user_id ? allUsers.find(u => u.id === gsmData.user_id) : undefined;
      const oldTariff = editingGSMLine.tariff;
      const newTariff = tariffs.find(t => t.id === gsmData.tariff_id);
      
      let changeDetails = [];
      
      // Check user assignment change
      if (editingGSMLine.user_id !== gsmData.user_id) {
        if (!oldUser && newUser) {
          changeDetails.push(`Kullanıcı ataması: Atanmamış → ${newUser.name}`);
        } else if (oldUser && !newUser) {
          changeDetails.push(`Kullanıcı ataması: ${oldUser.name} → Atanmamış`);
        } else if (oldUser && newUser) {
          changeDetails.push(`Kullanıcı ataması: ${oldUser.name} → ${newUser.name}`);
        }
      }
      
      // Check tariff change
      if (editingGSMLine.tariff_id !== gsmData.tariff_id) {
        if (oldTariff && newTariff) {
          changeDetails.push(`Tarife değişikliği: ${oldTariff.name} (₺${oldTariff.price}) → ${newTariff.name} (₺${newTariff.price})`);
        }
      }
      
      // Check status change
      if (editingGSMLine.status !== gsmData.status) {
        const oldStatusText = getStatusText(editingGSMLine.status);
        const newStatusText = getStatusText(gsmData.status);
        changeDetails.push(`Durum değişikliği: ${oldStatusText} → ${newStatusText}`);
        
        // Special notification for status changes
        const statusNotification = {
          id: notificationCenter.length + 2,
          title: 'GSM Hat Durumu Değişti',
          message: `${gsmData.phone_number} hattının durumu ${newStatusText} olarak güncellendi`,
          type: 'info' as const,
          category: 'system' as const,
          priority: 'normal' as const,
          is_read: false,
          is_dismissed: false,
          action_url: '/gsm-lines',
          action_text: 'GSM Hatlarını Görüntüle',
          user_id: currentUser.id,
          user: currentUser,
          created_by: currentUser.id,
          created_by_user: currentUser,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        notificationCenter.unshift(statusNotification);
      }

      // Update existing GSM line
      const updatedGSMLines = gsmLinesData.map(line => 
        line.id === editingGSMLine.id 
          ? {
              ...line,
              phone_number: gsmData.phone_number,
              tariff_id: gsmData.tariff_id,
              tariff: tariffs.find(t => t.id === gsmData.tariff_id),
              company_id: gsmData.company_id,
              company: companies.find(c => c.id === gsmData.company_id),
              user_id: gsmData.user_id || undefined,
              user: gsmData.user_id ? allUsers.find(u => u.id === gsmData.user_id) : undefined,
              location_id: gsmData.location_id,
              location: locations.find(l => l.id === gsmData.location_id),
              pin_code: gsmData.pin_code,
              puk_code: gsmData.puk_code,
              status: gsmData.status,
              description: gsmData.description,
              updated_at: new Date().toISOString()
            }
          : line
      );
      setGsmLinesData(updatedGSMLines);
      
      // Log with detailed changes
      const baseMessage = `${currentUser?.name} "${gsmData.phone_number}" GSM hattını güncelledi`;
      const detailedMessage = changeDetails.length > 0 
        ? `${baseMessage}. Değişiklikler: ${changeDetails.join(', ')}`
        : baseMessage;
      
      logActivity('update_gsm_line', detailedMessage, gsmData.phone_number);
    } else {
      // Add new GSM line
      const newGSMLine: GSMLine = {
        id: Math.max(...gsmLinesData.map(g => g.id)) + 1,
        phone_number: gsmData.phone_number,
        tariff_id: gsmData.tariff_id,
        tariff: tariffs.find(t => t.id === gsmData.tariff_id),
        company_id: gsmData.company_id,
        company: companies.find(c => c.id === gsmData.company_id),
        user_id: gsmData.user_id || undefined,
        user: gsmData.user_id ? allUsers.find(u => u.id === gsmData.user_id) : undefined,
        location_id: gsmData.location_id,
        location: locations.find(l => l.id === gsmData.location_id),
        pin_code: gsmData.pin_code,
        puk_code: gsmData.puk_code,
        status: gsmData.status,
        description: gsmData.description,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setGsmLinesData([...gsmLinesData, newGSMLine]);
      
      logActivity(
        'create_gsm_line',
        `${currentUser?.name} "${gsmData.phone_number}" GSM hattını oluşturdu`,
        gsmData.phone_number
      );
    }
    
    setShowModal(false);
    setGsmData({
      phone_number: '',
      tariff_id: 0,
      company_id: 0,
      user_id: 0,
      location_id: 0,
      pin_code: '',
      puk_code: '',
      status: 'active',
      description: ''
    });
  };

  const handleCreateUser = async () => {
    // Validation
    if (!newUserData.name || !newUserData.email || !newUserData.location_id) {
      showGlobalNotification.error(t('common.error'), t('gsm.errUserRequired'));
      return;
    }

    try {
      // Create user via API
      const userData = {
        name: newUserData.name,
        email: newUserData.email,
        role: newUserData.role,
        location_id: newUserData.location_id,
        phone: newUserData.phone,
        title: newUserData.title,
        password: newUserData.password || 'demo123'
      };
      
      const createdUser = await apiService.createUser(userData);
      
      // Reload users data
      await loadData();
      
      // Auto-assign to GSM form
      setGsmData({...gsmData, user_id: createdUser.id});
      
      showGlobalNotification.success(t('common.success'), t('gsm.successUserCreated'));
      
      setShowUserModal(false);
      setNewUserData({
        name: '',
        email: '',
        location_id: 0,
        phone: '',
        title: '',
        role: 'calisan',
        password: ''
      });
    } catch (error: any) {
      console.error('Error creating user:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('gsm.errCreateUser'));
    }
  };



  const handleQuickAssign = (gsmLine: GSMLine) => {
    setSelectedGSMLineForAssign(gsmLine);
    setUserSearchTerm('');
    setSelectedUserId(0);
    setShowQuickAssignModal(true);
  };

  const handleQuickAssignSave = async () => {
    if (!selectedGSMLineForAssign || selectedUserId === 0) {
      showGlobalNotification.error(t('common.error'), t('gsm.errSelectUser'));
      return;
    }

    // Check if GSM line already has a user assigned
    if (selectedGSMLineForAssign.user_id) {
      const currentUser = allUsers.find(u => u.id === selectedGSMLineForAssign.user_id);
      const newUser = allUsers.find(u => u.id === selectedUserId);
      
      if (selectedGSMLineForAssign.user_id === selectedUserId) {
        showGlobalNotification.warning(t('common.warning'), t('gsm.warnAlreadyAssigned'));
        return;
      }
      
      const confirmChange = confirm(
        language === 'en'
          ? `This GSM line is currently assigned to "${currentUser?.name}". Are you sure you want to change the assignment to "${newUser?.name}"?`
          : `Bu GSM hattı şu anda "${currentUser?.name}" kullanıcısına atanmış. Atamayı "${newUser?.name}" kullanıcısına değiştirmek istediğinizden emin misiniz?`
      );
      
      if (!confirmChange) {
        return;
      }
    }

    try {
      await apiService.quickAssignGSMLine(selectedGSMLineForAssign.id, selectedUserId);
      await loadData();
      
      const assignedUser = allUsers.find(u => u.id === selectedUserId);
      showGlobalNotification.success(
        t('common.success'),
        language === 'en'
          ? `GSM line assigned to "${assignedUser?.name}"`
          : `GSM hattı "${assignedUser?.name}" kullanıcısına atandı`
      );
      
      setShowQuickAssignModal(false);
      setSelectedGSMLineForAssign(null);
      setSelectedUserId(0);
    } catch (error: any) {
      console.error('Error assigning GSM line:', error);
      showGlobalNotification.error(t('common.error'), error.message || t('gsm.errAssignLine'));
    }
  };


  const handleDelete = async (id: number) => {
    const gsmLineToDelete = gsmLinesData.find(g => g.id === id);
    if (!gsmLineToDelete) return;
    
    if (confirm(t('gsm.confirmDelete'))) {
      try {
        await apiService.deleteGSMLine(id);
        await loadData();
        showGlobalNotification.success(t('common.success'), t('gsm.successDelete'));
      } catch (error: any) {
        console.error('Error deleting GSM line:', error);
        showGlobalNotification.error(t('common.error'), error.message || t('gsm.errDeleteLine'));
      }
    }
  };

  const togglePinPuk = (id: number) => {
    setShowPinPuk(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  // Export functions with Turkish character support
  const exportToCSV = () => {
    const headers = language === 'en'
      ? ['GSM Number', 'User', 'Company', 'Location', 'Tariff', 'Monthly Fee', 'Status', 'PIN', 'PUK', 'Description']
      : ['GSM Numarası', 'Kullanıcı', 'Şirket', 'Lokasyon', 'Tarife', 'Aylık Ücret', 'Durum', 'PIN', 'PUK', 'Açıklama'];
    const csvData = filteredGSMLines.map(line => [
      line.phone_number,
      line.user?.name || t('gsm.unassigned'),
      line.company?.name || '',
      line.location?.name || '',
      line.tariff?.name || '',
      `₺${Number(line.tariff?.price || 0).toFixed(2)}`,
      getStatusText(line.status),
      line.pin_code,
      line.puk_code,
      line.description || ''
    ]);

    // Add BOM for Turkish character support
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers, ...csvData]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `gsm-hatlari-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showGlobalNotification.success(t('common.success'), t('gsm.successExportCsv'));
  };

  const exportToExcel = () => {
    // Create Excel-compatible CSV with proper encoding
    const headers = language === 'en'
      ? ['GSM Number', 'User', 'Company', 'Location', 'Tariff', 'Monthly Fee', 'Status', 'PIN', 'PUK', 'Description', 'Created Date']
      : ['GSM Numarası', 'Kullanıcı', 'Şirket', 'Lokasyon', 'Tarife', 'Aylık Ücret', 'Durum', 'PIN', 'PUK', 'Açıklama', 'Oluşturulma Tarihi'];
    const excelData = filteredGSMLines.map(line => [
      line.phone_number,
      line.user?.name || t('gsm.unassigned'),
      line.company?.name || '',
      line.location?.name || '',
      line.tariff?.name || '',
      Number(line.tariff?.price || 0).toFixed(2),
      getStatusText(line.status),
      line.pin_code,
      line.puk_code,
      line.description || '',
      new Date(line.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')
    ]);

    // Excel format with BOM
    const BOM = '\uFEFF';
    const excelContent = BOM + [headers, ...excelData]
      .map(row => row.map(cell => `"${cell}"`).join('\t'))
      .join('\n');

    const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `gsm-hatlari-${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showGlobalNotification.success(t('common.success'), t('gsm.successExportExcel'));
  };

  const exportToPDF = () => {
    const isEn = language === 'en';
    const locale = isEn ? 'en-US' : 'tr-TR';
    // Create HTML content for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${isEn ? 'GSM Lines List' : 'GSM Hatları Listesi'}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; text-align: center; margin-bottom: 30px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 12px; }
          th { background-color: #f2f2f2; font-weight: bold; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .header-info { margin-bottom: 20px; }
          .export-date { text-align: right; color: #666; font-size: 12px; }
        </style>
      </head>
      <body>
        <h1>${isEn ? 'GSM Lines List' : 'GSM Hatları Listesi'}</h1>
        <div class="header-info">
          <div class="export-date">${isEn ? 'Report Date' : 'Rapor Tarihi'}: ${new Date().toLocaleDateString(locale)} ${new Date().toLocaleTimeString(locale)}</div>
          <p><strong>${isEn ? 'Total Lines' : 'Toplam Hat Sayısı'}:</strong> ${filteredGSMLines.length}</p>
          <p><strong>${isEn ? 'Active Lines' : 'Aktif Hatlar'}:</strong> ${filteredGSMLines.filter(l => l.status === 'active').length}</p>
          <p><strong>${isEn ? 'Total Monthly Cost' : 'Toplam Aylık Maliyet'}:</strong> ₺${filteredGSMLines.reduce((sum, l) => sum + Number(l.tariff?.price || 0), 0).toFixed(2)}</p>
        </div>
        <table>
          <thead>
            <tr>
              <th>${isEn ? 'GSM Number' : 'GSM Numarası'}</th>
              <th>${isEn ? 'User' : 'Kullanıcı'}</th>
              <th>${isEn ? 'Company' : 'Şirket'}</th>
              <th>${isEn ? 'Location' : 'Lokasyon'}</th>
              <th>${isEn ? 'Tariff' : 'Tarife'}</th>
              <th>${isEn ? 'Monthly Fee' : 'Aylık Ücret'}</th>
              <th>${isEn ? 'Status' : 'Durum'}</th>
              <th>${isEn ? 'Description' : 'Açıklama'}</th>
            </tr>
          </thead>
          <tbody>
            ${filteredGSMLines.map(line => `
              <tr>
                <td>${line.phone_number}</td>
                <td>${line.user?.name || (isEn ? 'Unassigned' : 'Atanmamış')}</td>
                <td>${line.company?.name || ''}</td>
                <td>${line.location?.name || ''}</td>
                <td>${line.tariff?.name || ''}</td>
                <td>₺${Number(line.tariff?.price || 0).toFixed(2)}</td>
                <td>${getStatusText(line.status)}</td>
                <td>${line.description || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    // Create and download PDF
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `gsm-hatlari-${new Date().toISOString().split('T')[0]}.html`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showGlobalNotification.info(t('gsm.exportPDF'), t('gsm.pdfInfo'));
  };

  const clearFilters = () => {
    setFilterLocation(0);
    setFilterUser(0);
    setFilterStatus('all');
    setFilterCompany(0);
    setFilterTariff(0);
    setFilterLongUnassigned(false);
    setSearchTerm('');
    setSortField('');
    setSortDirection('asc');
    setCurrentPage(1);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedGSMLines = [...filteredGSMLines].sort((a, b) => {
    if (!sortField) return 0;
    
    let aValue: any;
    let bValue: any;
    
    switch (sortField) {
      case 'phone_number':
        aValue = a.phone_number;
        bValue = b.phone_number;
        break;
      case 'user':
        aValue = a.user?.name || '';
        bValue = b.user?.name || '';
        break;
      case 'company':
        aValue = a.company?.name || '';
        bValue = b.company?.name || '';
        break;
      case 'tariff':
        aValue = a.tariff?.name || '';
        bValue = b.tariff?.name || '';
        break;
      case 'location':
        aValue = a.location?.name || '';
        bValue = b.location?.name || '';
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      default:
        return 0;
    }
    
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = String(aValue || '').toLowerCase();
      bValue = String(bValue || '').toLowerCase();
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedGSMLines.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedGSMLines = sortedGSMLines.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const saveGsmSettings = async () => {
    try {
      await Promise.all([
        apiService.upsertSystemSetting('gsm_empty_line_tariff_id', {
          setting_value: String(emptyLineTariffId),
          setting_type: 'number',
          category: 'general',
          description: 'Boş/Atanmamış GSM hatları için beklenen tarife ID\'si'
        }),
        apiService.upsertSystemSetting('gsm_excluded_statuses', {
          setting_value: JSON.stringify(excludedStatuses),
          setting_type: 'json',
          category: 'general',
          description: 'Boş hat uyarısından muaf tutulan GSM hat durumları'
        })
      ]);
      showGlobalNotification.success(t('common.success'), language === 'en' ? 'GSM settings saved' : 'GSM ayarları kaydedildi');
      setShowGsmSettingsModal(false);
    } catch (error: any) {
      showGlobalNotification.error(t('common.error'), error.message || (language === 'en' ? 'Failed to save settings' : 'Ayarlar kaydedilemedi'));
    }
  };

  // 3 aydan uzun süredir atanmamış veya boş olan hatlar (istisna durumlar hariç)
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
  const longUnassignedLines = (gsmLinesData || []).filter(line => {
    if (excludedStatuses.includes(line.status)) return false;
    const isUnassignedOrAvailable = !line.user_id || line.status === 'available';
    const isOldEnough = new Date(line.updated_at) < threeMonthsAgo;
    return isUnassignedOrAvailable && isOldEnough;
  });

  // Yanlış tarifede olan boş/atanmamış hatlar (istisna durumlar hariç)
  const isWrongTariff = (line: any) => {
    if (emptyLineTariffId === 0) return false;
    if (excludedStatuses.includes(line.status)) return false;
    const isUnassignedOrAvailable = !line.user_id || line.status === 'available';
    return isUnassignedOrAvailable && line.tariff_id !== emptyLineTariffId;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      case 'reserved':
        return 'bg-yellow-100 text-yellow-800';
      case 'available':
        return 'bg-primary/20 text-primary-dark';
      case 'transferred':
        return 'bg-purple-100 text-purple-800';
      case 'forwarded':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return t('gsm.statusActive');
      case 'inactive': return t('gsm.statusInactive');
      case 'suspended': return t('gsm.statusSuspended');
      case 'reserved': return t('gsm.statusReserved');
      case 'available': return t('gsm.statusAvailable');
      case 'transferred': return t('gsm.statusTransferred');
      case 'forwarded': return t('gsm.statusForwarded');
      default: return t('gsm.statusUnknown');
    }
  };

  const filteredUsers = allUsers.filter(user => 
    String(user.name || '').toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    String(user.email || '').toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          <span className="ml-2 text-gray-600 dark:text-gray-400">{t('gsm.loading')}</span>
        </div>
      </div>
    );
  }

  const openLineHistory = (line: GSMLine) => {
    setSelectedGSMLineForHistory(line);
    setShowAllHistory(false);
    setShowHistoryModal(true);
    apiService.getGSMLineHistory(line.id)
      .then(data => {
        setGsmLineHistory(data);
      })
      .catch(err => {
        console.error('History API error:', err);
        setGsmLineHistory([]);
      });
  };

  // Satır işlem butonları: masaüstü tablo ve mobil kart görünümünde ortak
  const renderLineActions = (line: GSMLine, variant: 'table' | 'card') => {
    const btn = variant === 'card'
      ? 'min-h-[44px] min-w-[44px] rounded-lg flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700'
      : 'p-1.5 min-h-[32px] min-w-[32px] rounded flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700';
    const icon = variant === 'card' ? 'w-5 h-5' : 'w-4 h-4';
    return (
      <>
        <button
          onClick={() => openLineHistory(line)}
          className={`${btn} text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100`}
          title={t('gsm.btnHistory')}
          aria-label={t('gsm.btnHistory')}
        >
          <History className={icon} />
        </button>
        <button
          onClick={() => handleEdit(line)}
          className={`${btn} text-primary hover:text-primary-dark`}
          title={t('common.edit')}
          aria-label={t('common.edit')}
        >
          <Edit2 className={icon} />
        </button>
        <button
          onClick={() => handleDelete(line.id)}
          className={`${btn} ${variant === 'card' ? 'ml-auto' : 'ml-2'} text-red-600 hover:text-red-900`}
          title={t('common.delete')}
          aria-label={t('common.delete')}
        >
          <Trash2 className={icon} />
        </button>
      </>
    );
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('gsm.pageTitle')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('gsm.pageSubtitle')}</p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-3 w-full sm:w-auto">
          {/* Export Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] text-sm w-full sm:w-auto"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('gsm.exportBtn')}
              <ChevronDown className="w-4 h-4 ml-2" />
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                <div className="py-2">
                  <button
                    onClick={() => {
                      exportToCSV();
                      setShowExportMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                  >
                    <FileText className="w-4 h-4 mr-3" />
                    {t('gsm.exportCSV')}
                  </button>
                  <button
                    onClick={() => {
                      exportToExcel();
                      setShowExportMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-3" />
                    {t('gsm.exportExcel')}
                  </button>
                  <button
                    onClick={() => {
                      exportToPDF();
                      setShowExportMenu(false);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
                  >
                    <FileText className="w-4 h-4 mr-3" />
                    {t('gsm.exportPDF')}
                  </button>
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={() => setShowGsmSettingsModal(true)}
            className="flex items-center justify-center px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors min-h-[44px] text-sm w-full sm:w-auto"
          >
            <Settings className="w-4 h-4 mr-2" />
            {language === 'en' ? 'GSM Settings' : 'GSM Ayarları'}
          </button>
          <button
            onClick={() => setShowSubscriptionModal(true)}
            className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors min-h-[44px] text-sm w-full sm:w-auto"
          >
            <FileText className="w-4 h-4 mr-2" />
            {language === 'en' ? 'New Subscription' : 'Yeni Abonelik'}
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px] text-sm w-full sm:w-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('gsm.newLine')}
          </button>
          <button
            onClick={handleImport}
            className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] text-sm w-full sm:w-auto"
          >
            <Upload className="w-4 h-4 mr-2" />
            {t('gsm.csvImportBtn')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 lg:grid-cols-9 gap-2 sm:gap-6 mb-6">
        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] sm:min-h-[100px]"
          onClick={() => {
            setFilterStatus('all');
            setFilterLocation(0);
            setFilterUser(0);
            setSearchTerm('');
          }}
          title={language === 'en' ? 'Show all lines' : 'Tüm hatları göster'}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between h-full">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm leading-tight font-medium text-gray-600 dark:text-gray-400 mb-1">{t('gsm.statTotal')}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-gray-100">{gsmLinesData.length}</p>
            </div>
            <div className="sm:w-12 sm:h-12 bg-red-100 rounded-lg hidden sm:flex items-center justify-center">
              <Phone className="w-4 h-4 sm:w-6 sm:h-6 text-red-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] sm:min-h-[100px]"
          onClick={() => {
            setFilterStatus('active');
            setFilterLocation(0);
            setFilterUser(0);
            setSearchTerm('');
          }}
          title={language === 'en' ? 'Filter active lines' : 'Aktif hatları filtrele'}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between h-full">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm leading-tight font-medium text-gray-600 dark:text-gray-400 mb-1">{t('gsm.statActive')}</p>
              <p className="text-lg sm:text-2xl font-bold text-green-600">
                {(gsmLinesData || []).filter(g => g.status === 'active').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg hidden sm:flex items-center justify-center">
              <Phone className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] sm:min-h-[100px]"
          onClick={() => {
            setFilterStatus('all');
            setFilterLocation(0);
            setFilterUser(-1);
            setSearchTerm('');
          }}
          title={language === 'en' ? 'Filter unassigned lines' : 'Atanmamış hatları filtrele'}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between h-full">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm leading-tight font-medium text-gray-600 dark:text-gray-400 mb-1">{t('gsm.statUnassigned')}</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-600 dark:text-gray-400">
                {(gsmLinesData || []).filter(g => !g.user_id).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg hidden sm:flex items-center justify-center">
              <User className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] sm:min-h-[100px]"
          onClick={() => {
            setFilterStatus('all');
            setFilterLocation(0);
            setFilterUser(0);
            setSearchTerm('');
            // Atanmış hatlar için özel filtre
            const assignedFilter = (gsmLinesData || []).filter(g => g.user_id);
            setFilterUser(assignedFilter.length > 0 ? assignedFilter[0].user_id : 0);
          }}
          title={language === 'en' ? 'Show assigned lines' : 'Atanmış hatları göster'}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between h-full">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm leading-tight font-medium text-gray-600 dark:text-gray-400 mb-1">{t('gsm.statAssigned')}</p>
              <p className="text-lg sm:text-2xl font-bold text-primary">
                {(gsmLinesData || []).filter(g => g.user_id).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary/20 rounded-lg hidden sm:flex items-center justify-center">
              <User className="w-6 h-6 text-primary" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] sm:min-h-[100px]"
          onClick={() => {
            setFilterStatus('reserved');
            setFilterLocation(0);
            setFilterUser(0);
            setSearchTerm('');
          }}
          title={language === 'en' ? 'Filter reserved lines' : 'Rezerve hatları filtrele'}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between h-full">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm leading-tight font-medium text-gray-600 dark:text-gray-400 mb-1">{t('gsm.statReserved')}</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-600">
                {(gsmLinesData || []).filter(g => g.status === 'reserved').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg hidden sm:flex items-center justify-center">
              <Phone className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] sm:min-h-[100px]"
          onClick={() => {
            setFilterStatus('available');
            setFilterLocation(0);
            setFilterUser(0);
            setSearchTerm('');
          }}
          title={language === 'en' ? 'Filter available lines' : 'Boş hatları filtrele'}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between h-full">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm leading-tight font-medium text-gray-600 dark:text-gray-400 mb-1">{t('gsm.statAvailable')}</p>
              <p className="text-lg sm:text-2xl font-bold text-orange-600">
                {(gsmLinesData || []).filter(g => g.status === 'available').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg hidden sm:flex items-center justify-center">
              <Phone className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] sm:min-h-[100px]"
          onClick={() => {
            setFilterStatus('transferred');
            setFilterLocation(0);
            setFilterUser(0);
            setSearchTerm('');
          }}
          title={language === 'en' ? 'Filter transferred lines' : 'Devredilen hatları filtrele'}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between h-full">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm leading-tight font-medium text-gray-600 dark:text-gray-400 mb-1">{t('gsm.statTransferred')}</p>
              <p className="text-lg sm:text-2xl font-bold text-purple-600">
                {(gsmLinesData || []).filter(g => g.status === 'transferred').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg hidden sm:flex items-center justify-center">
              <Phone className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] sm:min-h-[100px]"
          onClick={() => {
            setFilterStatus('forwarded');
            setFilterLocation(0);
            setFilterUser(0);
            setSearchTerm('');
          }}
          title={language === 'en' ? 'Filter forwarded lines' : 'Yönlendirilen hatları filtrele'}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between h-full">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm leading-tight font-medium text-gray-600 dark:text-gray-400 mb-1">{t('gsm.statForwarded')}</p>
              <p className="text-lg sm:text-2xl font-bold text-indigo-600">
                {(gsmLinesData || []).filter(g => g.status === 'forwarded').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg hidden sm:flex items-center justify-center">
              <Phone className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </button>

        <button
          type="button"
          className={`w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm border-2 p-3 sm:p-6 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] sm:min-h-[100px] ${filterLongUnassigned ? 'border-amber-500' : 'border-amber-300'}`}
          onClick={() => {
            setFilterLongUnassigned(true);
            setFilterStatus('all');
            setFilterLocation(0);
            setFilterUser(0);
            setFilterCompany(0);
            setFilterTariff(0);
            setSearchTerm('');
            setCurrentPage(1);
          }}
          title={language === 'en' ? 'Lines unassigned/available for 3+ months' : '3 aydan uzun süredir boş/atanmamış hatlar'}
        >
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-between h-full">
            <div className="text-center sm:text-left">
              <p className="text-xs sm:text-sm leading-tight font-medium text-amber-700 dark:text-amber-400 mb-1">
                {language === 'en' ? '3+ Mon. Empty' : '3+ Ay Boş'}
              </p>
              <p className="text-lg sm:text-2xl font-bold text-amber-600">
                {longUnassignedLines.length}
              </p>
            </div>
            <div className="sm:w-12 sm:h-12 bg-amber-100 rounded-lg hidden sm:flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 sm:w-6 sm:h-6 text-amber-600" />
            </div>
          </div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1 lg:min-w-[240px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('gsm.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => {
                let value = e.target.value;
                
                // Eğer sadece rakam giriyorsa ve GSM numarası gibi görünüyorsa otomatik formatla
                if (/^\d+$/.test(value) && value.length >= 10) {
                  const formatted = normalizePhoneNumber(value);
                  // Eğer formatlanmış versiyon farklıysa, kullanıcıya ipucu olarak göster
                  if (formatted !== value && formatted.startsWith('+90')) {
                    // Placeholder'ı güncelle ama değeri değiştirme
                    e.target.placeholder = `${language === 'en' ? 'Searching:' : 'Aranıyor:'} ${formatted}`;
                  }
                } else {
                  e.target.placeholder = t('gsm.searchPlaceholder');
                }
                
                setSearchTerm(value);
              }}
              className="pl-10 pr-4 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent w-full"
            />
          </div>

          <CollapsibleFilters
            activeCount={[filterLocation !== 0, filterCompany !== 0, filterUser !== 0, filterStatus !== 'all', filterTariff !== 0, filterLongUnassigned].filter(Boolean).length}
            onClear={clearFilters}
            desktopClassName="md:flex md:flex-wrap md:items-center md:gap-4"
          >
          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(parseInt(e.target.value))}
            className="px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value={0}>{t('gsm.filterAllLocations')}</option>
            {locations.map(location => (
              <option key={location.id} value={location.id}>{location.name}</option>
            ))}
          </select>

          <select
            value={filterCompany}
            onChange={(e) => setFilterCompany(parseInt(e.target.value))}
            className="px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value={0}>{t('gsm.filterAllCompanies')}</option>
            {companies.map(company => (
              <option key={company.id} value={company.id}>{company.name}</option>
            ))}
          </select>

          <select
            value={filterUser}
            onChange={(e) => setFilterUser(parseInt(e.target.value))}
            className="px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value={0}>{t('gsm.filterAllUsers')}</option>
            <option value={-1}>{t('gsm.filterUnassigned')}</option>
            {allUsers.map(user => (
              <option key={user.id} value={user.id}>{user.name}</option>
            ))}
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value="all">{t('gsm.filterAllStatuses')}</option>
            <option value="active">{t('gsm.statusActive')}</option>
            <option value="inactive">{t('gsm.statusInactive')}</option>
            <option value="suspended">{t('gsm.statusSuspended')}</option>
            <option value="reserved">{t('gsm.statusReserved')}</option>
            <option value="available">{t('gsm.statusAvailable')}</option>
            <option value="transferred">{t('gsm.statusTransferred')}</option>
            <option value="forwarded">{t('gsm.statusForwarded')}</option>
          </select>

          <select
            value={filterTariff}
            onChange={(e) => { setFilterTariff(parseInt(e.target.value)); setCurrentPage(1); }}
            className="px-3 py-2 min-h-[44px] border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          >
            <option value={0}>{language === 'en' ? 'All Tariffs' : 'Tüm Tarifeler'}</option>
            {tariffs.map(tariff => (
              <option key={tariff.id} value={tariff.id}>{tariff.name}</option>
            ))}
          </select>

          <button
            onClick={clearFilters}
            className="hidden md:flex px-4 py-2 min-h-[44px] bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors items-center"
          >
            <X className="w-4 h-4 mr-2" />
            {t('gsm.clearFilters')}
          </button>
          </CollapsibleFilters>
        </div>
      </div>

      {/* Long Unassigned Filter Banner */}
      {filterLongUnassigned && (
        <div className="mb-4 flex items-center gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 rounded-lg px-4 py-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <span className="text-sm text-amber-800 dark:text-amber-300 flex-1">
            {language === 'en'
              ? '3+ months unassigned/available lines are shown. Lines without the configured empty-line tariff are marked.'
              : '3 aydan uzun süredir boş/atanmamış hatlar gösteriliyor. Yapılandırılan boş hat tarifesinde olmayanlar işaretlendi.'}
          </span>
          <button
            onClick={() => setFilterLongUnassigned(false)}
            className="-m-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-amber-600 hover:text-amber-800 dark:text-amber-400"
            aria-label={t('common.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Results Info */}
      <div className="mb-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {language === 'en'
            ? `Showing ${startIndex + 1}-${Math.min(endIndex, sortedGSMLines.length)} of ${sortedGSMLines.length} GSM lines`
            : `Toplam ${sortedGSMLines.length} GSM hattından ${startIndex + 1}-${Math.min(endIndex, sortedGSMLines.length)} arası gösteriliyor`}
          {(searchTerm || filterLocation || filterUser || filterStatus !== 'all' || filterCompany) && (
            <span className="ml-2 text-primary">
              ({language === 'en' ? `filtered from ${gsmLinesData.length} total lines` : `${gsmLinesData.length} toplam hattan filtrelendi`})
            </span>
          )}
        </div>
        <div className="hidden sm:block text-sm text-gray-500 dark:text-gray-400">
          {t('gsm.page')} {currentPage} / {totalPages}
        </div>
      </div>

      {/* GSM Lines Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Mobil kart görünümü */}
        <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
          {paginatedGSMLines.map((line) => (
            <div key={line.id} className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center min-w-0">
                  <Phone className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
                  <span className="text-base font-medium text-gray-900 dark:text-gray-100 break-all">{line.phone_number}</span>
                </div>
                <span className={`flex-shrink-0 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(line.status)}`}>
                  {getStatusText(line.status)}
                </span>
              </div>

              <div className="mt-3 flex items-center min-w-0">
                {line.user ? (
                  <>
                    {line.user.avatar ? (
                      <img className="w-8 h-8 rounded-full mr-2 flex-shrink-0" src={line.user.avatar} alt={line.user.name} />
                    ) : (
                      <div className="w-8 h-8 rounded-full mr-2 flex-shrink-0 bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600">
                        {line.user.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{line.user.name}</div>
                      {line.user.title && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 break-words">{line.user.title}</div>
                      )}
                    </div>
                  </>
                ) : (
                  <span className="text-sm text-gray-400">{t('gsm.unassigned')}</span>
                )}
              </div>

              <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
                {line.company?.name && (
                  <>
                    <dt className="text-gray-500 dark:text-gray-400">{t('gsm.colCompany')}</dt>
                    <dd className="text-gray-900 dark:text-gray-100 break-words">{line.company.name}</dd>
                  </>
                )}
                {line.location?.name && (
                  <>
                    <dt className="text-gray-500 dark:text-gray-400">{t('gsm.colLocation')}</dt>
                    <dd className="text-gray-900 dark:text-gray-100 break-words">{line.location.name}</dd>
                  </>
                )}
                {line.tariff && (
                  <>
                    <dt className="text-gray-500 dark:text-gray-400">{t('gsm.colTariff')}</dt>
                    <dd className="text-gray-900 dark:text-gray-100 break-words">
                      <span className="inline-flex items-center gap-1">
                        {line.tariff.name}
                        {isWrongTariff(line) && (
                          <span title={language === 'en' ? 'Wrong tariff for empty line' : 'Boş hat için yanlış tarife'}>
                            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          </span>
                        )}
                      </span>
                      <span className="text-gray-500 dark:text-gray-400"> • ₺{Number(line.tariff.price || 0).toFixed(2)}{t('gsm.monthlyFee')}</span>
                    </dd>
                  </>
                )}
                <dt className="text-gray-500 dark:text-gray-400 self-center">PIN/PUK</dt>
                <dd className="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                  <button
                    type="button"
                    onClick={() => togglePinPuk(line.id)}
                    className="-my-2 min-h-[44px] min-w-[44px] -ml-3 flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
                    aria-label={showPinPuk[line.id]
                      ? (language === 'en' ? 'Hide PIN/PUK' : 'PIN/PUK gizle')
                      : (language === 'en' ? 'Show PIN/PUK' : 'PIN/PUK göster')}
                  >
                    {showPinPuk[line.id] ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                  {showPinPuk[line.id] && (
                    <span className="text-xs">PIN: {line.pin_code || '-'} • PUK: {line.puk_code || '-'}</span>
                  )}
                </dd>
              </dl>

              <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center gap-1">
                {renderLineActions(line, 'card')}
              </div>
            </div>
          ))}
        </div>

        {/* Table View */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full table-auto divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('phone_number')}
                >
                  <div className="flex items-center">
                    {t('gsm.colGsmNo')}
                    {sortField === 'phone_number' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('user')}
                >
                  <div className="flex items-center">
                    {t('gsm.colUser')}
                    {sortField === 'user' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('company')}
                >
                  <div className="flex items-center">
                    {t('gsm.colCompany')}
                    {sortField === 'company' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('location')}
                >
                  <div className="flex items-center">
                    {t('gsm.colLocation')}
                    {sortField === 'tariff' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('tariff')}
                >
                  <div className="flex items-center">
                    {t('gsm.colTariff')}
                    {sortField === 'location' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700 select-none"
                  onClick={() => handleSort('status')}
                >
                  <div className="flex items-center">
                    {t('gsm.colStatus')}
                    {sortField === 'status' && (
                      <span className="ml-1">
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  PIN/PUK
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('gsm.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {paginatedGSMLines.map((line) => (
                <tr key={line.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700">
                  <td className="px-3 py-3">
                    <div>
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 mr-2" />
                        <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                          {line.phone_number}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {line.user ? (
                      <div className="flex items-center">
                        {line.user.avatar ? (
                          <img
                            className="w-8 h-8 sm:w-10 sm:h-10 rounded-full mr-2"
                            src={line.user.avatar}
                            alt={line.user.name}
                          />
                        ) : (
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full mr-2 bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400">
                            {line.user.name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                            {line.user.name}
                          </div>
                          <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                            {line.user.title}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs sm:text-sm text-gray-400">{t('gsm.unassigned')}</span>
                    )}
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <Building2 className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {line.company?.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-900 dark:text-gray-100">
                        {line.location?.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div>
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {line.tariff?.name}
                        </span>
                        {isWrongTariff(line) && (
                          <span title={language === 'en' ? 'Wrong tariff for empty line' : 'Boş hat için yanlış tarife'}>
                            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        ₺{Number(line.tariff?.price || 0).toFixed(2)}{t('gsm.monthlyFee')}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(line.status)}`}>
                      {getStatusText(line.status)}
                    </span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => togglePinPuk(line.id)}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                      >
                        {showPinPuk[line.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      {showPinPuk[line.id] && (
                        <div className="text-xs">
                          <div>PIN: {line.pin_code}</div>
                          <div>PUK: {line.puk_code}</div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex gap-1 justify-end">
                      {renderLineActions(line, 'table')}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        
        {/* Pagination */}
        {totalPages > 1 && (
          <>
          <div className="sm:hidden px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
            <button
              onClick={goToPrevious}
              disabled={currentPage === 1}
              className="px-4 min-h-[44px] text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('gsm.btnPrev')}
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('gsm.page')} {currentPage} / {totalPages}
            </span>
            <button
              onClick={goToNext}
              disabled={currentPage === totalPages}
              className="px-4 min-h-[44px] text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('gsm.btnNext')}
            </button>
          </div>
          <div className="hidden sm:flex px-6 py-4 border-t border-gray-200 dark:border-gray-700 items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={goToPrevious}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('gsm.btnPrev')}
              </button>
              <div className="flex items-center mx-4">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => goToPage(pageNum)}
                      className={`px-3 py-1 mx-1 text-sm border rounded-md ${
                        currentPage === pageNum
                          ? 'bg-red-600 text-white border-red-600'
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={goToNext}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('gsm.btnNext')}
              </button>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {t('gsm.perPage')}
            </div>
          </div>
          </>
        )}
      </div>

      {/* GSM Line Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingGSMLine ? t('gsm.modalEditTitle') : t('gsm.modalNewTitle')}
              </h2>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('gsm.fieldGsmNumber')}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none">
                      +90
                    </span>
                    <input
                      type="text"
                      value={gsmData.phone_number.replace('+90 ', '')}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        
                        // Maksimum 10 haneli
                        if (value.length > 10) {
                          value = value.substring(0, 10);
                        }
                        
                        // Format: 549 802 72 11
                        let formatted = '';
                        if (value.length > 0) {
                          if (value.length <= 3) {
                            formatted = value;
                          } else if (value.length <= 6) {
                            formatted = value.substring(0, 3) + ' ' + value.substring(3);
                          } else if (value.length <= 8) {
                            formatted = value.substring(0, 3) + ' ' + value.substring(3, 6) + ' ' + value.substring(6);
                          } else {
                            formatted = value.substring(0, 3) + ' ' + value.substring(3, 6) + ' ' + value.substring(6, 8) + ' ' + value.substring(8);
                          }
                        }
                        
                        // State'e +90 ile birlikte kaydet
                        setGsmData({...gsmData, phone_number: formatted ? '+90 ' + formatted : ''});
                      }}
                      className="w-full pl-12 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      placeholder="549 802 72 11"
                      maxLength={13}
                      minLength={10}
                    />
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('gsm.fieldGsmHint')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('gsm.fieldTariff')}
                  </label>
                  <select
                    value={gsmData.tariff_id}
                    onChange={(e) => setGsmData({...gsmData, tariff_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value={0}>{t('gsm.fieldTariffPlaceholder')}</option>
                    {tariffs.map((tariff) => (
                      <option key={tariff.id} value={tariff.id}>
                        {tariff.name} - ₺{tariff.price}/ay
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('gsm.fieldCompany')}
                  </label>
                  <select
                    value={gsmData.company_id}
                    onChange={(e) => setGsmData({...gsmData, company_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value={0}>{t('gsm.fieldCompanyPlaceholder')}</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('gsm.fieldLocation')}
                  </label>
                  <select
                    value={gsmData.location_id}
                    onChange={(e) => setGsmData({...gsmData, location_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value={0}>{t('gsm.fieldLocationPlaceholder')}</option>
                    {locations.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('gsm.fieldUserAssign')}
                  </label>
                  <div className="space-y-2">
                    <div className="flex space-x-2">
                      <select
                        value={gsmData.user_id}
                        onChange={(e) => setGsmData({...gsmData, user_id: parseInt(e.target.value)})}
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                        disabled={gsmData.status === 'reserved' || gsmData.status === 'available' || gsmData.status === 'transferred' || gsmData.status === 'forwarded'}
                        size={1}
                      >
                        <option value={0}>
                          {(gsmData.status === 'reserved' || gsmData.status === 'available' || gsmData.status === 'transferred' || gsmData.status === 'forwarded') ? t('gsm.userUnassignedDue') : t('gsm.unassigned')}
                        </option>
                        {!(gsmData.status === 'reserved' || gsmData.status === 'available' || gsmData.status === 'transferred' || gsmData.status === 'forwarded') && allUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </select>
                      <button
                        onClick={() => setShowUserModal(true)}
                        className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center"
                        title={t('gsm.btnQuickCreateUser')}
                        disabled={gsmData.status === 'reserved' || gsmData.status === 'available' || gsmData.status === 'transferred' || gsmData.status === 'forwarded'}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                    {!(gsmData.status === 'reserved' || gsmData.status === 'available' || gsmData.status === 'transferred' || gsmData.status === 'forwarded') && (
                      <p className="text-xs text-primary">
                        {t('gsm.userAssignHint')}
                      </p>
                    )}
                    {(gsmData.status === 'reserved' || gsmData.status === 'available' || gsmData.status === 'transferred' || gsmData.status === 'forwarded') && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {gsmData.status === 'reserved' ? t('gsm.statusReserved') : gsmData.status === 'available' ? t('gsm.statusAvailable') : gsmData.status === 'transferred' ? t('gsm.statusTransferred') : t('gsm.statusForwarded')} {t('gsm.statusRestrictionHint')}
                      </p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('gsm.fieldStatus')}
                  </label>
                  <select
                    value={gsmData.status}
                    onChange={(e) => {
                      const newStatus = e.target.value as 'active' | 'inactive' | 'suspended' | 'reserved' | 'available' | 'transferred' | 'forwarded';
                      const updatedData = {...gsmData, status: newStatus};

                      // Auto-clear user assignment for reserved/available/transferred/forwarded status
                      if (newStatus === 'reserved' || newStatus === 'available' || newStatus === 'transferred' || newStatus === 'forwarded') {
                        updatedData.user_id = 0;
                      }

                      // Clear forwarded_to_gsm_id if status is not forwarded
                      if (newStatus !== 'forwarded') {
                        updatedData.forwarded_to_gsm_id = 0;
                      }

                      setGsmData(updatedData);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="active">{t('gsm.statusActive')}</option>
                    <option value="inactive">{t('gsm.statusInactive')}</option>
                    <option value="suspended">{t('gsm.statusSuspended')}</option>
                    <option value="reserved">{t('gsm.statusReserved')}</option>
                    <option value="available">{t('gsm.statusAvailable')}</option>
                    <option value="transferred">{t('gsm.statusTransferred')}</option>
                    <option value="forwarded">{t('gsm.statusForwarded')}</option>
                  </select>
                  {(gsmData.status === 'reserved' || gsmData.status === 'available' || gsmData.status === 'transferred' || gsmData.status === 'forwarded') && (
                    <p className="text-xs text-orange-600 mt-1">
                      {t('gsm.statusClearHint')}
                    </p>
                  )}
                </div>

                {gsmData.status === 'forwarded' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('gsm.fieldForwardedGsm')}
                    </label>
                    <div className="relative forwarded-gsm-dropdown">
                      <input
                        type="text"
                        value={forwardedGsmSearchTerm}
                        onChange={(e) => {
                          setForwardedGsmSearchTerm(e.target.value);
                          setShowForwardedGsmDropdown(true);
                        }}
                        onFocus={() => setShowForwardedGsmDropdown(true)}
                        placeholder={t('gsm.forwardSearchPlaceholder')}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                      {showForwardedGsmDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                          {gsmLinesData
                            .filter(line => {
                              if (line.id === editingGSMLine?.id) return false;
                              if (!forwardedGsmSearchTerm) return true;
                              const searchLower = String(forwardedGsmSearchTerm || '').toLowerCase();
                              return (
                                String(line.phone_number || '').toLowerCase().includes(searchLower) ||
                                String(line.company?.name || '').toLowerCase().includes(searchLower) ||
                                String(line.user?.name || '').toLowerCase().includes(searchLower)
                              );
                            })
                            .slice(0, 50) // İlk 50 sonucu göster
                            .map((line) => (
                              <div
                                key={line.id}
                                onClick={() => {
                                  setGsmData({...gsmData, forwarded_to_gsm_id: line.id});
                                  setForwardedGsmSearchTerm(`${line.phone_number} - ${line.company?.name}`);
                                  setShowForwardedGsmDropdown(false);
                                }}
                                className={`px-3 py-2 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                  line.id === gsmData.forwarded_to_gsm_id 
                                    ? 'bg-primary/10 border-primary/30' 
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-700 dark:bg-gray-700'
                                }`}
                              >
                                <div className="flex flex-col">
                                  <div className="flex items-center justify-between">
                                    <span className={`font-medium ${
                                      line.id === gsmData.forwarded_to_gsm_id ? 'text-primary-dark' : 'text-gray-900 dark:text-gray-100'
                                    }`}>
                                      {line.phone_number}
                                    </span>
                                    {line.id === gsmData.forwarded_to_gsm_id && (
                                      <span className="text-xs text-primary font-medium">✓ {t('gsm.forwardSelected')}</span>
                                    )}
                                  </div>
                                  <span className={`text-sm ${
                                    line.id === gsmData.forwarded_to_gsm_id ? 'text-primary-dark' : 'text-gray-600 dark:text-gray-400'
                                  }`}>
                                    {line.company?.name}
                                  </span>
                                  {line.user?.name && (
                                    <span className={`text-xs ${
                                      line.id === gsmData.forwarded_to_gsm_id ? 'text-primary' : 'text-gray-500 dark:text-gray-400'
                                    }`}>
                                      {t('gsm.forwardUser')} {line.user.name}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          {gsmLinesData.filter(line => {
                            if (line.id === editingGSMLine?.id) return false;
                            if (!forwardedGsmSearchTerm) return true;
                            const searchLower = String(forwardedGsmSearchTerm || '').toLowerCase();
                            return (
                              String(line.phone_number || '').toLowerCase().includes(searchLower) ||
                              String(line.company?.name || '').toLowerCase().includes(searchLower) ||
                              String(line.user?.name || '').toLowerCase().includes(searchLower)
                            );
                          }).length === 0 && (
                            <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">
                              {t('gsm.forwardNoResult')}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {gsmData.forwarded_to_gsm_id > 0 && (
                      <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                        <span className="text-sm text-green-800">
                          {t('gsm.forwardChosenLabel')} {gsmLinesData.find(line => line.id === gsmData.forwarded_to_gsm_id)?.phone_number} -
                          {gsmLinesData.find(line => line.id === gsmData.forwarded_to_gsm_id)?.company?.name}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('gsm.fieldPin')}
                  </label>
                  <input
                    type="text"
                    value={gsmData.pin_code}
                    onChange={(e) => setGsmData({...gsmData, pin_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="1234"
                    maxLength={4}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('gsm.fieldPuk')}
                  </label>
                  <input
                    type="text"
                    value={gsmData.puk_code}
                    onChange={(e) => setGsmData({...gsmData, puk_code: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="12345678"
                    maxLength={8}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('gsm.fieldDescription')}
                </label>
                <textarea
                  value={gsmData.description}
                  onChange={(e) => setGsmData({...gsmData, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder={t('gsm.descriptionPlaceholder')}
                />
              </div>
            </div>

            <div className="sticky bottom-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 min-h-[44px] bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                {editingGSMLine ? t('gsm.btnUpdate') : t('common.save')}
              </button>
            </div>
          </div>
        </Modal>

      {/* New User Modal - Fixed z-index */}
      <Modal isOpen={showUserModal} onClose={() => setShowUserModal(false)} zIndex="z-[70]">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('gsm.userModalTitle')}
                </h2>
                <button
                  onClick={() => setShowUserModal(false)}
                  className="-m-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('gsm.fieldFullName')}
                </label>
                <input
                  type="text"
                  value={newUserData.name}
                  onChange={(e) => setNewUserData({...newUserData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Mehmet Demir"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('gsm.fieldEmail')}
                </label>
                <input
                  type="email"
                  value={newUserData.email}
                  onChange={(e) => setNewUserData({...newUserData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="mehmet@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('gsm.fieldLocation')}
                </label>
                <select
                  value={newUserData.location_id}
                  onChange={(e) => setNewUserData({...newUserData, location_id: parseInt(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value={0}>{t('gsm.fieldLocationPlaceholder')}</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('gsm.fieldPhone')}
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 pointer-events-none">
                    +90
                  </span>
                  <input
                    type="text"
                    value={newUserData.phone.replace('+90 ', '')}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, '');
                      
                      // Maksimum 10 haneli
                      if (value.length > 10) {
                        value = value.substring(0, 10);
                      }
                      
                      // Format: 549 802 72 11
                      let formatted = '';
                      if (value.length > 0) {
                        if (value.length <= 3) {
                          formatted = value;
                        } else if (value.length <= 6) {
                          formatted = value.substring(0, 3) + ' ' + value.substring(3);
                        } else if (value.length <= 8) {
                          formatted = value.substring(0, 3) + ' ' + value.substring(3, 6) + ' ' + value.substring(6);
                        } else {
                          formatted = value.substring(0, 3) + ' ' + value.substring(3, 6) + ' ' + value.substring(6, 8) + ' ' + value.substring(8);
                        }
                      }
                      
                      // State'e +90 ile birlikte kaydet
                      setNewUserData({...newUserData, phone: formatted ? '+90 ' + formatted : ''});
                    }}
                    className="w-full pl-12 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="549 802 72 11"
                    maxLength={13}
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('gsm.fieldGsmHint')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('gsm.fieldTitle')}
                </label>
                <input
                  type="text"
                  value={newUserData.title}
                  onChange={(e) => setNewUserData({...newUserData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Teknisyen"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('gsm.fieldPassword')}
                </label>
                <input
                  type="password"
                  value={newUserData.password}
                  onChange={(e) => setNewUserData({...newUserData, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder={t('gsm.passwordHint')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('gsm.fieldRole')}
                </label>
                <select
                  value={newUserData.role}
                  onChange={(e) => setNewUserData({...newUserData, role: e.target.value as any})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="calisan">{language === 'en' ? 'Employee' : 'Çalışan'}</option>
                  <option value="teknisyen">{language === 'en' ? 'Technician' : 'Teknisyen'}</option>
                  {(currentUser?.role === 'admin' || currentUser?.role === 'superadmin') && (
                    <option value="admin">Admin</option>
                  )}
                  {currentUser?.role === 'superadmin' && (
                    <option value="superadmin">Super Admin</option>
                  )}
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {currentUser?.role === 'superadmin'
                    ? (language === 'en' ? 'You can select all permission levels' : 'Tüm yetki seviyelerini seçebilirsiniz')
                    : currentUser?.role === 'admin'
                    ? (language === 'en' ? 'You can select admin and below permissions' : 'Admin ve altı yetkileri seçebilirsiniz')
                    : (language === 'en' ? 'You can only create employee permission level' : 'Sadece çalışan yetki seviyesi oluşturabilirsiniz')}
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowUserModal(false)}
                className="px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateUser}
                className="px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                {t('gsm.btnCreateUser')}
              </button>
            </div>
          </div>
        </Modal>

      {/* Import Modal */}
      <Modal isOpen={showImportModal} onClose={() => setShowImportModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('gsm.csvModalTitle')}
                </h2>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="-m-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

            </div>
            
            <div className="p-4 sm:p-6 space-y-6">
              {/* Template Download */}
              <div className="bg-primary/10 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-primary-dark mb-1">
                      {t('gsm.csvStep1Title')}
                    </h3>
                    <p className="text-sm text-primary">
                      {t('gsm.csvStep1Desc')}
                    </p>
                  </div>
                  <button
                    onClick={downloadTemplate}
                    className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {t('gsm.btnDownloadTemplate')}
                  </button>
                </div>
              </div>

              {/* File Upload */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t('gsm.csvStep2Title')}
                </h3>
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="import-file"
                  />
                  <label
                    htmlFor="import-file"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload className="w-12 h-12 text-gray-400 mb-4" />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                      {t('gsm.fileDropText')}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {t('gsm.fileDropFormats')}
                    </span>
                  </label>
                </div>
                
                {importData.file && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center">
                      <FileText className="w-5 h-5 text-green-600 mr-2" />
                      <span className="text-sm font-medium text-green-800">
                        {importData.file.name}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Validation Errors */}
              {importData.errors.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg">
                  <h3 className="text-sm font-medium text-red-800 mb-2">
                    {t('gsm.validationErrors')} ({importData.errors.length})
                  </h3>
                  <div className="max-h-32 overflow-y-auto">
                    {importData.errors.map((error, index) => (
                      <p key={index} className="text-sm text-red-600 mb-1">
                        • {error}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview Data */}
              {importData.preview.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('gsm.csvStep3Title')} ({importData.preview.length} {language === 'en' ? 'records' : 'kayıt'})
                  </h3>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="overflow-auto max-h-[50vh] sm:max-h-64">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('gsm.colGsmNo')}</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('gsm.colTariff')}</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('gsm.colCompany')}</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('gsm.colLocation')}</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('gsm.colUser')}</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">{t('gsm.colStatus')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {importData.preview.slice(0, 10).map((row, index) => (
                            <tr key={index}>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.gsm_number}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.tariff_name}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.company_name}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.location_name}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.user_name || '-'}</td>
                              <td className="px-3 py-2 text-sm text-gray-900 dark:text-gray-100">{row.status || 'active'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {importData.preview.length > 10 && (
                      <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 text-center">
                        ... {language === 'en' ? `and ${importData.preview.length - 10} more records` : `ve ${importData.preview.length - 10} kayıt daha`}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Import Instructions */}
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-yellow-800 mb-2">
                  {t('gsm.importRulesTitle')}
                </h3>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {language === 'en' ? (
                    <>
                      <li>• GSM number must be 13 digits (0XXX XXX XX XXXX)</li>
                      <li>• Tariff, company and location must exist in the system</li>
                      <li>• If user doesn't exist, will be auto-created (email required)</li>
                      <li>• Reserved/Available lines cannot be assigned to users</li>
                      <li>• PIN code must be 4 digits, PUK code must be 8 digits</li>
                    </>
                  ) : (
                    <>
                      <li>• GSM numarası 13 haneli olmalı (0XXX XXX XX XXXX)</li>
                      <li>• Tarife, şirket ve lokasyon sistemde mevcut olmalı</li>
                      <li>• Kullanıcı yoksa otomatik oluşturulur (e-posta gerekli)</li>
                      <li>• Rezerve/Boş durumundaki hatlar kullanıcıya atanamaz</li>
                      <li>• PIN kodu 4 haneli, PUK kodu 8 haneli olmalı</li>
                    </>
                  )}
                </ul>
              </div>

            </div>

            <div className="sticky bottom-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={processImport}
                disabled={importData.preview.length === 0 || importData.errors.length > 0 || importData.processing}
                className="px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importData.processing ? t('gsm.btnProcessing') : (language === 'en' ? `Import ${importData.preview.length} Records` : `${importData.preview.length} Kaydı İçe Aktar`)}
              </button>
            </div>
          </div>
        </Modal>

      {/* Quick Assign Modal */}
      <Modal isOpen={showQuickAssignModal} onClose={() => setShowQuickAssignModal(false)}>
          {selectedGSMLineForAssign && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('gsm.assignModalTitle')}
                </h2>
                <button
                  onClick={() => setShowQuickAssignModal(false)}
                  className="-m-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4">
              <div className="bg-primary/10 p-3 rounded-lg">
                <div className="flex items-center">
                  <Phone className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-primary-dark">
                    {t('gsm.assignGsmLine')} {selectedGSMLineForAssign.phone_number}
                  </span>
                </div>
                {selectedGSMLineForAssign.user && (
                  <div className="mt-2 text-sm text-primary-dark">
                    {t('gsm.assignCurrentUser')} {selectedGSMLineForAssign.user.name}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('gsm.assignSearchLabel')}
                </label>
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder={t('gsm.assignSearchPlaceholder')}
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent w-full"
                  />
                </div>
                
                <div className="max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  {filteredUsers.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      {userSearchTerm ? t('gsm.assignUserNotFound') : t('gsm.assignUserLoading')}
                    </div>
                  ) : (
                    filteredUsers.map((user) => (
                      <div
                        key={user.id}
                        onClick={() => setSelectedUserId(user.id)}
                        className={`p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 border-b border-gray-100 last:border-b-0 ${
                          selectedUserId === user.id ? 'bg-green-50 border-green-200' : ''
                        }`}
                      >
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            {user.avatar ? (
                              <img
                                className="w-8 h-8 rounded-full"
                                src={user.avatar}
                                alt={user.name}
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-gray-600 dark:text-gray-400">
                                {user.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="ml-3 flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {user.email}
                            </div>
                            <div className="text-xs text-gray-400">
                              {user.title} • {user.location?.name}
                            </div>
                          </div>
                          {selectedUserId === user.id && (
                            <div className="flex-shrink-0">
                              <div className="w-4 h-4 bg-green-600 rounded-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white dark:bg-gray-800 rounded-full"></div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            <div className="sticky bottom-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowQuickAssignModal(false)}
                className="px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleQuickAssignSave}
                disabled={selectedUserId === 0}
                className="px-4 py-2 min-h-[44px] bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('gsm.btnAssign')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* NEW History Modal */}
      <Modal isOpen={showHistoryModal} onClose={() => setShowHistoryModal(false)}>
          {selectedGSMLineForHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('gsm.historyModalTitle')} - {selectedGSMLineForHistory.phone_number}
                </h2>
                <button
                  onClick={() => {
                    setShowHistoryModal(false);
                    setSelectedGSMLineForHistory(null);
                    setGsmLineHistory([]);
                  }}
                  className="-m-2 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                {language === 'en' ? `Total ${gsmLineHistory.length} records found` : `Toplam ${gsmLineHistory.length} kayıt bulundu`}
              </div>
              
              {gsmLineHistory.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">{t('gsm.historyLoading')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {(showAllHistory ? gsmLineHistory : gsmLineHistory.slice(0, 3)).map((item, index) => (
                    <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="font-medium text-gray-900 dark:text-gray-100">{item.description}</h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(item.created_at).toLocaleString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <p>{t('gsm.historyUser')} {item.user_name}</p>
                        <p>IP: {item.ip_address}</p>
                        {item.changes && (
                          <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded">
                            <strong>{t('gsm.historyChanges')}</strong>
                            {Object.entries(item.changes).map(([key, value]) => (
                              <div key={key} className="text-xs">
                                {key}: {value as string}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {/* Daha fazla butonu */}
                  {gsmLineHistory.length > 3 && !showAllHistory && (
                    <div className="text-center pt-4">
                      <button
                        onClick={() => setShowAllHistory(true)}
                        className="px-4 py-2 text-primary hover:text-primary-dark hover:bg-primary/10 rounded-lg transition-colors text-sm font-medium"
                      >
                        {t('gsm.btnShowMore')} ({gsmLineHistory.length - 3} {language === 'en' ? 'more records' : 'kayıt daha'})
                      </button>
                    </div>
                  )}
                  
                  {/* Daha az göster butonu */}
                  {showAllHistory && gsmLineHistory.length > 3 && (
                    <div className="text-center pt-4">
                      <button
                        onClick={() => setShowAllHistory(false)}
                        className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 rounded-lg transition-colors text-sm font-medium"
                      >
                        {t('gsm.btnShowLess')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="sticky bottom-0 z-10 bg-white dark:bg-gray-800 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => {
                  setShowHistoryModal(false);
                  setSelectedGSMLineForHistory(null);
                  setGsmLineHistory([]);
                }}
                className="px-4 py-2 min-h-[44px] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('gsm.btnClose')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* GSM Ayarları Modal */}
      <Modal isOpen={showGsmSettingsModal} onClose={() => setShowGsmSettingsModal(false)}>
        <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
            <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {language === 'en' ? 'GSM Settings' : 'GSM Ayarları'}
            </h2>
          </div>

          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {language === 'en' ? 'Empty Line Tariff' : 'Boş Hat Tarifesi'}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                {language === 'en'
                  ? 'Select the expected tariff for unassigned/available GSM lines. Lines with a different tariff will be flagged with a warning.'
                  : 'Atanmamış veya Boş durumundaki GSM hatlarının olması gereken tarifesini seçin. Farklı tarifede olanlar uyarı ile işaretlenecektir.'}
              </p>
              <select
                value={emptyLineTariffId}
                onChange={(e) => setEmptyLineTariffId(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value={0}>{language === 'en' ? '-- Not configured --' : '-- Yapılandırılmadı --'}</option>
                {tariffs.map(tariff => (
                  <option key={tariff.id} value={tariff.id}>
                    {tariff.name} {tariff.price ? `(₺${Number(tariff.price).toFixed(2)})` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {language === 'en' ? 'Excluded Statuses' : 'İstisna Durumlar'}
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                {language === 'en'
                  ? 'Lines with these statuses will be excluded from the empty-line warning and the 3+ months card.'
                  : 'Bu durumlardaki hatlar boş hat uyarısından ve 3+ ay kartından muaf tutulur.'}
              </p>
              <div className="space-y-2">
                {[
                  { value: 'inactive',    label: language === 'en' ? 'Inactive (Pasif)'         : 'Pasif' },
                  { value: 'suspended',   label: language === 'en' ? 'Suspended (Askıda)'        : 'Askıda' },
                  { value: 'reserved',    label: language === 'en' ? 'Reserved (Rezerve)'        : 'Rezerve' },
                  { value: 'transferred', label: language === 'en' ? 'Transferred (Devredildi)'  : 'Devredildi' },
                  { value: 'forwarded',   label: language === 'en' ? 'Forwarded (Yönlendirildi)' : 'Yönlendirildi' },
                ].map(({ value, label }) => (
                  <label key={value} className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={excludedStatuses.includes(value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setExcludedStatuses(prev => [...prev, value]);
                        } else {
                          setExcludedStatuses(prev => prev.filter(s => s !== value));
                        }
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
                  </label>
                ))}
              </div>
            </div>

            {emptyLineTariffId > 0 && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-300">
                <div className="flex items-center gap-2 mb-1">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span className="font-medium">
                    {language === 'en' ? 'Warning summary' : 'Uyarı özeti'}
                  </span>
                </div>
                <p>
                  {language === 'en'
                    ? `${(gsmLinesData || []).filter(line => isWrongTariff(line)).length} unassigned/available line(s) have a different tariff.`
                    : `${(gsmLinesData || []).filter(line => isWrongTariff(line)).length} adet boş/atanmamış hat yanlış tarifede.`}
                </p>
              </div>
            )}
          </div>

          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
            <button
              onClick={() => setShowGsmSettingsModal(false)}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {t('common.cancel') || (language === 'en' ? 'Cancel' : 'İptal')}
            </button>
            <button
              onClick={saveGsmSettings}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
            >
              {t('common.save') || (language === 'en' ? 'Save' : 'Kaydet')}
            </button>
          </div>
        </div>
      </Modal>
      <GSMNewSubscription
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        companies={companies}
        tariffs={tariffs}
        locations={locations}
        users={allUsers}
        onLineActivated={loadData}
      />
    </div>
  );
};

export default GSMLinesPage;